'use client';

import AiSuggestionChip from '@carvana/ds-ai-suggestion-chip';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import useComposerStarterQuestionRotation, {
  SLIDE_MS
} from '~/hooks/home/useComposerStarterQuestionRotation';
import styles from './HomeStarterQuestion.module.css';

const TYPING_CURSOR = '▏';

type OwnProps = {
  disabled?: boolean;
  onSelectAction: (question: string) => void;
  questions: ReadonlyArray<string>;
};

export default function HomeStarterQuestion({
  disabled = false,
  onSelectAction,
  questions
}: OwnProps) {
  const { currentQuestion, displayedText, sliding, typing } =
    useComposerStarterQuestionRotation(questions, !disabled);
  const hasNext = questions.length > 1;
  const pinnedQuestionRef = useRef<string | null>(null);
  const trackEvent = useTrackEvent();
  const trackRef = useRef<HTMLDivElement>(null);
  const [restingOffset, setRestingOffset] = useState<number | null>(null);

  // Height/offset are measured from the rendered chip, not hardcoded, so a DS
  // release can't silently break them. Applied as plain inline styles rather
  // than CSS custom properties + calc() — a real device showed no tip peek at
  // all with that approach, consistent with the custom-property update not
  // landing before calc() read it.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const viewport = track?.parentElement;
    const chipSlot = viewport?.parentElement;
    if (!track || !viewport || !chipSlot) return undefined;

    const children = Array.from(track.children) as Array<HTMLElement>;
    const current = hasNext ? children[1] : children[0];
    if (!current) return undefined;

    const verticalPadding =
      parseFloat(getComputedStyle(viewport).paddingTop) * 2;

    const measure = () => {
      const chipHeight = current.offsetHeight;

      // Includes verticalPadding (unlike the chip-only width below) so this
      // matches viewport's real painted height exactly — otherwise the
      // padding's worth of viewport overflow leaks past chipSlot's box into
      // Composer.tsx's slot row as scrollable overflow instead of visible
      // layout height, silently making that row scroll (REO-9216).
      chipSlot.style.height = `${chipHeight + verticalPadding}px`;
      chipSlot.style.width = `${current.offsetWidth}px`;
      viewport.style.height = `${chipHeight + verticalPadding}px`;

      if (hasNext) {
        setRestingOffset(current.offsetTop);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    children.forEach(child => observer.observe(child));

    return () => observer.disconnect();
  }, [currentQuestion, hasNext]);

  const handlePointerDown = useCallback(() => {
    pinnedQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    const question = pinnedQuestionRef.current ?? currentQuestion;
    pinnedQuestionRef.current = null;

    trackEvent('composer_shell_questions_click', {
      action: 'clicked',
      page: 'home',
      question
    });

    onSelectAction(question);
  }, [currentQuestion, disabled, onSelectAction, trackEvent]);

  return (
    <div
      className="flex shrink-0 items-center gap-sm"
      data-testid="home-starter-question"
    >
      <span className="text-text-strong t-label-sm">Get started</span>
      <div className={styles.chipSlot}>
        <div className={styles.viewport}>
          <div
            className={styles.track}
            ref={trackRef}
            style={
              hasNext && restingOffset !== null
                ? {
                    transform: `translateY(${sliding ? 0 : -restingOffset}px)`,
                    transition: sliding
                      ? `transform ${SLIDE_MS}ms ease-in-out`
                      : undefined
                  }
                : undefined
            }
          >
            {hasNext && (
              // Blank, not nextQuestion's full text — it's about to type from
              // empty the instant it lands, so showing it complete here just to
              // blank-and-retype would read as a glitch.
              <AiSuggestionChip
                aria-hidden
                data-analytics-id={null}
                iconLeft={false}
                label={' '}
                tabIndex={-1}
              />
            )}
            <AiSuggestionChip
              aria-busy={disabled}
              aria-label={currentQuestion}
              // trackEvent below already fires on click; a real analytics id
              // here would double-count the click via auto-instrumentation.
              data-analytics-id={null}
              data-testid="home-starter-question-chip"
              iconLeft={false}
              label={
                typing ? `${displayedText}${TYPING_CURSOR}` : displayedText
              }
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              testId="home-starter-question-chip"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
